import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { lazy, Suspense, useEffect } from "react";

// Declare prerenderReady on Window for Prerender.io integration
declare global {
  interface Window {
    prerenderReady?: boolean;
  }
}

// Lazy load all page components for better performance
const Index = lazy(() => import("./pages/Index"));
const Websites = lazy(() => import("./pages/Websites"));
const Systems = lazy(() => import("./pages/Systems"));
const MemberPulse = lazy(() => import("./pages/MemberPulse"));
const AIPoweredCRM = lazy(() => import("./pages/AIPoweredCRM"));
const Automations = lazy(() => import("./pages/Automations"));
const Ads = lazy(() => import("./pages/Ads"));
const DojoSocialSuite = lazy(() => import("./pages/DojoSocialSuite"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ExportCopy = lazy(() => import("./pages/ExportCopy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-primary">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();

  // Signal to Prerender.io that page is ready after meta tags are updated
  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.prerenderReady !== undefined) {
        window.prerenderReady = true;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop />
      {/* ExitIntentPopup disabled for Black Friday focus */}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/websites" element={<Websites />} />
          <Route path="/systems" element={<Systems />} />
          <Route path="/member-pulse" element={<MemberPulse />} />
          <Route path="/ai-powered-crm" element={<AIPoweredCRM />} />
          <Route path="/automations" element={<Automations />} />
          <Route path="/ads" element={<Ads />} />
          <Route path="/dojo-social-suite" element={<DojoSocialSuite />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/export-copy" element={<ExportCopy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
